import * as p from '@clack/prompts'
import { spawn } from 'node:child_process'
import path from 'node:path'
import type { DrizzlePushOptions, DrizzlePushResult } from '@cli-types/next-adapter/utils.js'
import { createDrizzleNoiseFilter } from './create-drizzle-noise-filter.js'
import { createTtyPromptErrorSuppressor } from './create-tty-prompt-error-suppressor.js'
import { resolveDrizzlePushStdio } from './resolve-drizzle-push-stdio.js'

export function runDrizzlePush(
  cwd: string,
  options: DrizzlePushOptions = {}
): Promise<DrizzlePushResult> {
  return new Promise((resolve) => {
    const drizzleBin = path.join(cwd, 'node_modules', '.bin', 'drizzle-kit')
    const child = spawn(drizzleBin, ['push', '--force'], {
      cwd,
      stdio: resolveDrizzlePushStdio(options.interactive),
      env: { ...process.env }
    })
    let stdout = ''
    let stderr = ''
    let outputNotified = false
    const notifyOutput = () => {
      if (outputNotified) return
      outputNotified = true
      options.onOutput?.()
    }
    const stderrTtySuppressor = createTtyPromptErrorSuppressor((chunk) => {
      notifyOutput()
      process.stderr.write(chunk)
    })
    const stdoutTtySuppressor = createTtyPromptErrorSuppressor((chunk) => {
      notifyOutput()
      process.stdout.write(chunk)
    })
    const stderrFilter = createDrizzleNoiseFilter((chunk) => {
      if (options.interactive) {
        stderrTtySuppressor.write(chunk)
        return
      }

      stderr += chunk
    })
    const stdoutFilter = createDrizzleNoiseFilter((chunk) => {
      if (options.interactive) {
        stdoutTtySuppressor.write(chunk)
        return
      }

      stdout += chunk
    })

    child.stdout?.on('data', (chunk: Buffer) => {
      stdoutFilter.write(chunk.toString())
    })
    child.stderr?.on('data', (chunk: Buffer) => {
      stderrFilter.write(chunk.toString())
    })
    child.on('close', (code, signal) => {
      stdoutFilter.end()
      stderrFilter.end()

      if (options.interactive) {
        if (code === 0) {
          resolve({ success: true, error: null })
          return
        }

        if (stdoutTtySuppressor.sawError() || stderrTtySuppressor.sawError()) {
          notifyOutput()
          p.log.info('Database changes need your input — continuing in drizzle-kit.')
          const attached = spawn(drizzleBin, ['push', '--force'], {
            cwd,
            stdio: 'inherit',
            env: { ...process.env }
          })
          attached.on('close', (attachedCode, attachedSignal) => {
            if (attachedCode === 0) {
              resolve({ success: true, error: null })
              return
            }
            resolve({
              success: false,
              error: attachedSignal
                ? `drizzle-kit push exited with signal ${attachedSignal}`
                : `drizzle-kit push exited with code ${attachedCode}`
            })
          })
          attached.on('error', (err) => {
            resolve({ success: false, error: err.message })
          })
          return
        }

        resolve({
          success: false,
          error: signal
            ? `drizzle-kit push exited with signal ${signal}`
            : `drizzle-kit push exited with code ${code}`
        })
        return
      }

      const output = `${stdout}\n${stderr}`.trim()
      const reportedError = output.match(/(?:^|\n)Error: [\s\S]*$/)?.[0]?.trimStart()

      if (code === 0 && !reportedError) {
        resolve({ success: true, error: null })
        return
      }

      resolve({
        success: false,
        error: reportedError ?? (stderr || stdout || `drizzle-kit push exited with code ${code}`)
      })
    })
    child.on('error', (err) => {
      resolve({ success: false, error: err.message })
    })
  })
}
